/* ════════════ FALLING CRATES ════════════
   The deps page's atmosphere: pixel packages drifting down the screen
   like an npm install in progress. Most are inert. Some are not.
   Roughly one in eight blinks red, because that's the point. */

import { onPage } from "./router";

const REDUCED = matchMedia("(prefers-reduced-motion: reduce)").matches;

function crates(signal: AbortSignal) {
  const c = document.querySelector<HTMLCanvasElement>("#crates")!;
  const ctx = c.getContext("2d")!;

  type Crate = {
    x: number;
    y: number;
    s: number; // crate size in px
    vy: number;
    bad: boolean; // compromised: blinks red
    phase: number;
    col: string;
  };
  let crates: Crate[] = [];
  const dimCols = ["#5a3a42", "#4a3038", "#6a4550"];

  const make = (anywhere = false): Crate => {
    const bad = Math.random() < 0.125;
    return {
      x: Math.random() * c.width,
      y: anywhere ? Math.random() * c.height : -10,
      s: Math.random() > 0.8 ? 8 : 6,
      vy: 0.3 + Math.random() * 0.7,
      bad,
      phase: Math.random() * Math.PI * 2,
      col: dimCols[(Math.random() * dimCols.length) | 0]!,
    };
  };

  const seed = () => {
    c.width = innerWidth;
    c.height = innerHeight;
    const count = Math.min(60, Math.floor((c.width * c.height) / 30000));
    crates = Array.from({ length: count }, () => make(true));
  };
  seed();
  addEventListener("resize", seed, { signal });

  const drawCrate = (cr: Crate, t: number) => {
    const blink = cr.bad ? 0.55 + 0.45 * Math.sin(t * 6 + cr.phase) : 0;
    ctx.globalAlpha = cr.bad ? 0.5 + 0.4 * blink : 0.5;
    ctx.fillStyle = cr.bad ? "#ff5d6e" : cr.col;
    const x = cr.x | 0;
    const y = cr.y | 0;
    // box with a lid line: reads as a parcel at 6-8 px
    ctx.fillRect(x, y, cr.s, cr.s);
    ctx.fillStyle = "#0d0508";
    ctx.fillRect(x, y + 2, cr.s, 1);
    if (cr.bad) {
      // a notch missing from the corner: something got in
      ctx.fillRect(x + cr.s - 2, y, 2, 2);
    }
  };

  let t = 0;
  const draw = () => {
    ctx.clearRect(0, 0, c.width, c.height);
    for (const cr of crates) drawCrate(cr, t);
    ctx.globalAlpha = 1;
  };

  if (REDUCED) {
    draw();
    return;
  }
  const tick = () => {
    if (signal.aborted) return;
    t += 0.016;
    for (const cr of crates) {
      cr.y += cr.vy;
      if (cr.y > c.height + 10) Object.assign(cr, make());
    }
    draw();
    requestAnimationFrame(tick);
  };
  tick();
}

/* ════════════ THE ARGUMENT, ANIMATED ════════════
   The page plays like a closing argument: a progress bar tracks how
   far through the case you are, evidence cards stamp in as they enter
   the viewport, and the hero numbers count up. With reduced motion
   (or no JS) everything is simply visible. */

function presentation(signal: AbortSignal) {
  if (REDUCED) return;
  document.body.classList.add("js-armed");

  /* scroll progress: how much of the argument has been made */
  const bar = document.createElement("div");
  bar.id = "argbar";
  document.body.appendChild(bar);
  const onScroll = () => {
    const max = document.documentElement.scrollHeight - innerHeight;
    bar.style.width = `${max > 0 ? (scrollY / max) * 100 : 0}%`;
  };
  addEventListener("scroll", onScroll, { passive: true, signal });
  onScroll();

  /* staggered reveals: titles and panels rise, evidence stamps down */
  const targets = document.querySelectorAll<HTMLElement>(
    "main .sec-title, main .sec-sub, main .panel, main .case, main .objs li, main .ai-list li, main .debuffs li",
  );
  const io = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        if (!e.isIntersecting) continue;
        (e.target as HTMLElement).classList.add("show");
        io.unobserve(e.target);
      }
    },
    { rootMargin: "0px 0px -8% 0px", threshold: 0.05 },
  );
  targets.forEach((el) => {
    el.classList.add("anim");
    // stagger within each parent so grids cascade instead of popping
    const sibs = Array.from(el.parentElement?.children ?? []);
    el.style.transitionDelay = `${Math.min(sibs.indexOf(el), 8) * 70}ms`;
    io.observe(el);
  });

  /* hero stats count up: "11", "2.6B", "19.7%" all animate */
  for (const b of document.querySelectorAll<HTMLElement>(".stat-strip b")) {
    const m = b.textContent?.match(/^([\d.]+)(.*)$/);
    if (!m) continue;
    const target = parseFloat(m[1]!);
    const suffix = m[2] ?? "";
    const decimals = (m[1]!.split(".")[1] ?? "").length;
    const t0 = performance.now();
    const dur = 1400;
    const step = (t: number) => {
      if (signal.aborted) return;
      const k = Math.min(1, (t - t0) / dur);
      const eased = 1 - (1 - k) * (1 - k) * (1 - k);
      b.textContent = (target * eased).toFixed(decimals) + suffix;
      if (k < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }
}

onPage((signal) => {
  crates(signal);
  presentation(signal);
});
